import React, { useState, useEffect, useMemo } from 'react';
import AuctionChart from '../AuctionChart';
import { formatPrice } from '../../services/auctionService';
import { db } from '../../firebase';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import '../../Views/CarPage.css';
import './PcaScreenshots.css';
import carImage from '../../assets/images/72_911.png';
import MileageEstimator from '../MileageEstimator';
import ConditionEstimator from '../ConditionEstimator';

const PcaScreenshots = () => {
    const modelId = '99624sCoupe';
    const [auctionStats, setAuctionStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [auctionData, setAuctionData] = useState([]);
    const [timeframe, setTimeframe] = useState('12 Months');
    const [sortBy, setSortBy] = useState('miles');
    const [modelData, setModelData] = useState(null);
    const [modelNotFound, setModelNotFound] = useState(false);
    const [filters, setFilters] = useState({
        year: new Set(),
        colorFamily: new Set(),
        intColorFamily: new Set(),
        transmission: new Set(),
        options: new Set(),
        accident: new Set(),
        condition: new Set(),
        conditionType: new Set(),
        engine: new Set(),
        region: new Set(),
        owners: new Set(),
        modifier1: new Set(),
        modifier2: new Set(),
        modifier3: new Set(),
        modifier4: new Set(),
        modifier5: new Set(),
        auctionHouse: new Set(),
        auctionPlatform: new Set(),
        soldStatus: new Set()
    });
    const [uniqueValues, setUniqueValues] = useState({
        year: [],
        colorFamily: [],
        intColorFamily: [],
        transmission: [],
        options: [],
        accident: [],
        condition: [],
        conditionType: [],
        engine: [],
        region: [],
        owners: [],
        modifier1: [],
        modifier2: [],
        modifier3: [],
        modifier4: [],
        modifier5: [],
        auctionHouse: [],
        auctionPlatform: [],
        soldStatus: []
    });
    const [hoveredAuction, setHoveredAuction] = useState(null);
    const [hoveredFilter, setHoveredFilter] = useState({ category: null, value: null });
    const [previousPeriodStats, setPreviousPeriodStats] = useState(null);
    const [showConditionBands, setShowConditionBands] = useState(false);
    const [conditionBands, setConditionBands] = useState([]);
    const [showMileageBands, setShowMileageBands] = useState(false);
    const [mileageRegression, setMileageRegression] = useState(null);

    const categoryDisplayNames = {
        colorFamily: 'Exterior Color',
        intColorFamily: 'Interior Color',
        options: 'Notable Options/Mods',
        region: 'Region',
        owners: 'One-Owner',
        modifier1: 'Modifier',
        modifier2: 'Modifier',
        modifier3: 'Modifier',
        modifier4: 'Modifier',
        modifier5: 'Modifier',
        accident: 'Accident History',
        condition: 'Condition',
        conditionType: 'Condition Type',
        engine: 'Engine',
        auctionPlatform: 'Auction Platform'
    };

    // Function to get display value for filter options
    const getDisplayValue = (category, value) => {
        if (category === 'accident') {
            return value === 'TRUE' ? 'Yes' : 'No';
        }
        if (category === 'transmission') {
            return value.charAt(0).toUpperCase() + value.slice(1);
        }
        return value;
    };

    // Function to filter data based on timeframe
    const filterDataByTimeframe = (data, selectedTimeframe) => {
        const now = new Date();
        let startDate;

        switch (selectedTimeframe) {
            case '24 Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
                break;
            case '12 Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                break;
            case '6 Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                break;
            case '3 Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
                break;
            case '1 Month':
                startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                break;
            default:
                startDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
        }

        return data.filter(auction => {
            const auctionDate = auction.auctionDate?.toDate?.() || new Date(auction.auctionDate);
            return auctionDate >= startDate;
        });
    };

    // Function to filter data for the previous equivalent period
    const filterPreviousPeriod = (data, selectedTimeframe) => {
        const now = new Date();
        let currentStart, previousStart;

        switch (selectedTimeframe) {
            case '24 Months':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 48, now.getDate());
                break;
            case '12 Months':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
                break;
            case '6 Months':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                break;
            case '3 Months':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                break;
            case '1 Month':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
                break;
            default:
                currentStart = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
        }

        return data.filter(auction => {
            const auctionDate = auction.auctionDate?.toDate?.() || new Date(auction.auctionDate);
            return auctionDate >= previousStart && auctionDate < currentStart;
        });
    };

    // Function to calculate stats from filtered data
    const calculateStats = (data) => {
        let totalPrice = 0;
        let highestPrice = 0;
        let lowestPrice = Infinity;
        let soldCount = 0;

        data.forEach(auction => {
            const finalBid = parseFloat(auction.finalBid);
            if (auction.sold) {
                soldCount++;
                totalPrice += finalBid;
                highestPrice = Math.max(highestPrice, finalBid);
                lowestPrice = Math.min(lowestPrice, finalBid);
            }
        });

        return {
            sellThroughRate: data.length > 0 ? Number(((soldCount / data.length) * 100).toFixed(1)) : 0,
            averagePrice: soldCount > 0 ? totalPrice / soldCount : 0,
            highestPrice: highestPrice,
            lowestPrice: lowestPrice === Infinity ? 0 : lowestPrice
        };
    };

    // Function to get unique values and filter out categories with single/no values
    const getUniqueValues = (data, field) => {
        if (!data || data.length === 0) return [];

        if (field === 'soldStatus') {
            const values = new Set();
            data.forEach(item => {
                values.add(item.sold ? 'Sold' : 'Not Sold');
            });
            // Only return if there are both sold and unsold items
            return values.size > 1 ? Array.from(values).sort() : [];
        }

        if (field === 'options') {
            const values = new Set();
            data.forEach(item => {
                if (Array.isArray(item.options)) {
                    item.options.forEach(option => {
                        if (option && option.trim()) {
                            values.add(option.trim());
                        }
                    });
                }
            });
            // Only return if there are multiple options
            return values.size > 1 ? Array.from(values).sort() : [];
        }

        if (field === 'accident') {
            const values = new Set();
            data.forEach(item => {
                const value = item[field]?.toString();
                if (value) values.add(value);
            });
            // For boolean fields, only show if both TRUE and FALSE exist
            return values.has('TRUE') && values.has('FALSE') ? Array.from(values).sort() : [];
        }

        // For all other fields
        const values = new Set();
        data.forEach(item => {
            const value = item[field]?.toString();
            if (value) values.add(value);
        });

        // Only return if there are multiple values
        return values.size > 1 ? Array.from(values).sort() : [];
    };

    // Function to initialize filters with all values checked
    const initializeFilters = (data) => {
        const uniqueValuesObj = {};
        Object.keys(uniqueValues).forEach(field => {
            const values = getUniqueValues(data, field);
            // Only include fields that have multiple values
            if (values.length > 0) {
                uniqueValuesObj[field] = values;
            }
        });
        setUniqueValues(uniqueValuesObj);

        // Initialize all filters as checked
        const newFilters = {};
        Object.keys(uniqueValuesObj).forEach(field => {
            newFilters[field] = new Set(uniqueValuesObj[field]);
        });
        setFilters(newFilters);
    };

    // Function to apply filters to previous period data
    // Only excludes values the user explicitly unchecked (present in uniqueValues but not in filters)
    // Values that don't exist in the current period's uniqueValues are allowed through
    const applyFiltersForPreviousPeriod = (data) => {
        return data.filter(item => {
            return Object.keys(filters).every(field => {
                if (filters[field].size === 0) return true;
                if (field === 'soldStatus') return true;

                if (field === 'options') {
                    if (!Array.isArray(item.options) || item.options.length === 0) return true;
                    return item.options.every(option => {
                        const isKnownValue = uniqueValues[field]?.includes(option);
                        if (!isKnownValue) return true;
                        return filters[field].has(option);
                    });
                }

                const itemValue = item[field]?.toString();
                if (!itemValue) return true;
                const isKnownValue = uniqueValues[field]?.includes(itemValue);
                if (!isKnownValue) return true;
                return filters[field].has(itemValue);
            });
        });
    };

    // Function to apply filters to data
    const applyFilters = (data, includeSoldFilter = true) => {
        return data.filter(item => {
            return Object.keys(filters).every(field => {
                if (filters[field].size === 0) return true;

                if (field === 'soldStatus') {
                    if (!includeSoldFilter) return true;
                    const soldValue = item.sold ? 'Sold' : 'Not Sold';
                    return filters[field].has(soldValue);
                }

                if (field === 'options') {
                    // If item has no options, only include if this filter isn't being used
                    if (!Array.isArray(item.options) || item.options.length === 0) {
                        return true; // Include items without options
                    }

                    // For items with options, ALL of their options must be selected
                    // If any option the item has is unchecked, filter it out
                    return item.options.every(option =>
                        filters[field].has(option)
                    );
                }

                const itemValue = item[field]?.toString();
                // If the item doesn't have this field, include it (don't filter it out)
                if (!itemValue) return true;
                return filters[field].has(itemValue);
            });
        });
    };

    // Function to handle filter changes
    const handleFilterChange = (category, value, checked) => {
        setFilters(prev => {
            const newFilters = { ...prev };
            const newSet = new Set(prev[category]);

            if (checked) {
                newSet.add(value);
            } else {
                newSet.delete(value);
            }

            newFilters[category] = newSet;
            return newFilters;
        });
    };

    // Function to check if a filter value matches the hovered auction
    const isHighlighted = (category, value) => {
        if (!hoveredAuction) return false;

        if (category === 'soldStatus') {
            const soldValue = hoveredAuction.sold ? 'Sold' : 'Not Sold';
            return soldValue === value;
        }

        if (category === 'accident') {
            const accidentValue = hoveredAuction[category]?.toString();
            return accidentValue === value;
        }

        if (category === 'options') {
            return Array.isArray(hoveredAuction.options) &&
                   hoveredAuction.options.includes(value);
        }

        return hoveredAuction[category]?.toString() === value;
    };

    const completedAuctions = useMemo(() => {
        return auctionData.filter(a => a.finalBid != null && a.finalBid !== '');
    }, [auctionData]);

    // Get available values for the current timeframe
    const getTimeframeValues = useMemo(() => {
        if (!completedAuctions.length) return {};

        const timeframeData = filterDataByTimeframe(completedAuctions, timeframe);
        const fieldsToCheck = Object.keys(uniqueValues);
        const timeframeValues = {};

        fieldsToCheck.forEach(field => {
            if (field === 'soldStatus') {
                timeframeValues[field] = new Set(
                    timeframeData.map(item => item.sold ? 'Sold' : 'Not Sold')
                );
            } else if (field === 'options') {
                // Handle options as an array
                timeframeValues[field] = new Set();
                timeframeData.forEach(item => {
                    if (Array.isArray(item.options)) {
                        item.options.forEach(option => {
                            if (option && option.trim()) {
                                timeframeValues[field].add(option.trim());
                            }
                        });
                    }
                });
            } else {
                timeframeValues[field] = new Set(
                    timeframeData.map(item => item[field]?.toString()).filter(Boolean)
                );
            }
        });

        return timeframeValues;
    }, [completedAuctions, timeframe, uniqueValues]);

    const filteredData = useMemo(() => {
        const timeframeFiltered = filterDataByTimeframe(completedAuctions, timeframe);
        return applyFilters(timeframeFiltered);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completedAuctions, timeframe, filters]);

    // Stable normalized array for MileageEstimator. Without this memo, every
    // render produced a new array reference, retriggering the estimator's
    // regression useMemo, which called setMileageRegression and caused another
    // render — a tight loop that made the page sluggish.
    const mileageEstimatorAuctions = useMemo(() => (
        filteredData.map(a => ({
            ...a,
            miles: typeof a.miles === 'number'
                ? a.miles
                : parseInt(String(a.miles).replace(/[^0-9]/g, ''), 10) || 0,
            price: typeof a.finalBid === 'number'
                ? a.finalBid
                : parseFloat(a.finalBid) || 0
        }))
    ), [filteredData]);

    useEffect(() => {
        const fetchModelData = async () => {
            try {
                // Fetch model data from modelLines collection
                const modelQuery = query(
                    collection(db, 'modelLines'),
                    where('modelId', '==', modelId)
                );
                const modelSnapshot = await getDocs(modelQuery);

                if (modelSnapshot.empty) {
                    setModelNotFound(true);
                    setLoading(false);
                    return;
                }

                const modelData = modelSnapshot.docs[0].data();
                setModelData(modelData);

                // Set up real-time listener for auction data
                const auctionsRef = collection(db, 'auctions');
                const q = query(auctionsRef, where('modelId', '==', modelId));

                const unsubscribe = onSnapshot(q, (querySnapshot) => {
                    const auctionDataArray = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    setAuctionData(auctionDataArray);
                    initializeFilters(auctionDataArray);
                    setLoading(false);
                }, (error) => {
                    console.error('Error fetching auction data:', error);
                    setLoading(false);
                });

                // Return cleanup function to unsubscribe when component unmounts
                return unsubscribe;
            } catch (error) {
                console.error('Error fetching model data:', error);
                setLoading(false);
            }
        };

        const unsubscribe = fetchModelData();

        // Cleanup function
        return () => {
            if (unsubscribe && typeof unsubscribe.then === 'function') {
                unsubscribe.then(unsub => unsub && unsub());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (completedAuctions && completedAuctions.length > 0) {
            const timeframeData = filterDataByTimeframe(completedAuctions, timeframe);

            // Use timeframeData directly instead of applying filters for stats calculation
            // This ensures stats reflect all data in the selected timeframe
            const stats = calculateStats(timeframeData);
            setAuctionStats(stats);

            // Update uniqueValues for the new timeframe
            const uniqueValuesObj = {};
            const fieldsToCheck = [
                'year', 'colorFamily', 'intColorFamily', 'transmission',
                'options', 'accident', 'condition', 'conditionType', 'engine',
                'region', 'owners', 'modifier1', 'modifier2', 'modifier3', 'modifier4', 'modifier5',
                'auctionHouse', 'auctionPlatform', 'soldStatus'
            ];

            fieldsToCheck.forEach(field => {
                const values = getUniqueValues(timeframeData, field);
                // Only include fields that have multiple values
                if (values.length > 0) {
                    uniqueValuesObj[field] = values;
                }
            });

            setUniqueValues(uniqueValuesObj);

            // Update filters to only include values that exist in the new timeframe
            setFilters(prev => {
                const newFilters = {};
                Object.keys(uniqueValuesObj).forEach(field => {
                    newFilters[field] = new Set(uniqueValuesObj[field]);
                });
                return newFilters;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completedAuctions, timeframe]);

    useEffect(() => {
        if (completedAuctions && completedAuctions.length > 0) {
            const timeframeData = filterDataByTimeframe(completedAuctions, timeframe);
            const filteredTimeframeData = applyFilters(timeframeData, false);
            const stats = calculateStats(filteredTimeframeData);
            setAuctionStats(stats);

            // Calculate previous period stats with user's unchecked filters applied
            const prevData = filterPreviousPeriod(completedAuctions, timeframe);
            const filteredPrevData = applyFiltersForPreviousPeriod(prevData);
            const prevStats = calculateStats(filteredPrevData);
            prevStats.unitVolume = filteredPrevData.length;
            setPreviousPeriodStats(prevStats);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, completedAuctions, timeframe]);

    const handleTimeframeChange = (e) => {
        const newTimeframe = e.target.value;
        setTimeframe(newTimeframe);
    };

    // If model not found, render nothing
    if (modelNotFound) {
        return null;
    }

    // If still loading, show loading state
    if (loading) {
        return (
            <div className="pca-demo">
                <p style={{ textAlign: 'center' }}>Loading auction data...</p>
            </div>
        );
    }

    // Fallback stats so the page still renders when there is no auction data yet
    const safeStats = auctionStats || {
        sellThroughRate: 0,
        averagePrice: 0,
        lowestPrice: 0,
        highestPrice: 0,
        totalVolume: 0
    };

    return (
        <section className="pca-demo">
            <div className="pca-demo__badge-wrap">
                <div className="pca-demo__badge">Give It a Try</div>
            </div>
            <div className="betasml-carpage">
                <div className="pca-demo__toolbar">
                    <div className="dropdown-container">
                        <div className="toggle-container" style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: '15px'
                        }}>
                            <span style={{ marginRight: '8px' }}>Date</span>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={sortBy === 'miles'}
                                    onChange={(e) => setSortBy(e.target.checked ? 'miles' : 'date')}
                                />
                                <span className="slider round"></span>
                            </label>
                            <span style={{ marginLeft: '8px' }}>Miles</span>
                        </div>
                        <select
                            className="timeframe-select"
                            value={timeframe}
                            onChange={handleTimeframeChange}
                        >
                            <option>24 Months</option>
                            <option>12 Months</option>
                            <option>6 Months</option>
                            <option>3 Months</option>
                            <option>1 Month</option>
                        </select>
                    </div>
                </div>
                <main className="carpage-content">
                    {/* Left Panel */}
                    <section className="left-panel">
                        <div className="model-tile">
                            <div className="model-image-container">
                                <img
                                    src={modelData?.imageLink || carImage}
                                    alt={modelData ? `${modelData.modelLine} ${modelData.model}` : 'Loading...'}
                                    className="model-image"
                                    loading="lazy"
                                />
                            </div>
                            <div className="model-info">
                                <h3>{modelData ? `${modelData.modelLine} ${modelData.model}` : 'Loading...'}</h3>
                                <p>({modelData?.year || '...'})</p>
                            </div>
                        </div>

                        {modelData?.estimator === 'mileage' && (
                            <MileageEstimator
                                onGraphToggleChange={setShowMileageBands}
                                onRegressionChange={setMileageRegression}
                                filteredAuctions={mileageEstimatorAuctions}
                            />
                        )}

                        {modelData?.estimator === 'condition' && (
                            <ConditionEstimator
                                filteredAuctions={filteredData}
                                onGraphToggleChange={setShowConditionBands}
                                onBandsChange={setConditionBands}
                            />
                        )}

                        {/* Trend Analysis tile */}
                        {!(['12 Months', '6 Months', '3 Months', '1 Month'].includes(timeframe)) && (
                            <div className="trend-analysis-tile">
                                <h3>Trend Analysis</h3>
                                <p className="trend-subtitle">
                                    Select a shorter time period for Trend Analysis
                                </p>
                            </div>
                        )}
                        {(['12 Months', '6 Months', '3 Months', '1 Month'].includes(timeframe)) && (() => {
                            const hasPreviousPeriodData = (previousPeriodStats?.unitVolume || 0) > 0;
                            const volDelta = filteredData.length - (previousPeriodStats?.unitVolume || 0);
                            const strDelta = (auctionStats?.sellThroughRate || 0) - (previousPeriodStats?.sellThroughRate || 0);
                            const avgDelta = (auctionStats?.averagePrice || 0) - (previousPeriodStats?.averagePrice || 0);
                            const lowDelta = (auctionStats?.lowestPrice || 0) - (previousPeriodStats?.lowestPrice || 0);
                            const highDelta = (auctionStats?.highestPrice || 0) - (previousPeriodStats?.highestPrice || 0);

                            const deltaColor = (val) => val > 0 ? '#2ecda7' : val < 0 ? '#e85d75' : '#888';
                            const deltaSign = (val) => val > 0 ? '+' : '';
                            const renderDelta = (content, value) => {
                                if (!hasPreviousPeriodData) {
                                    return <p className="trend-delta" style={{ color: '#888' }}>--</p>;
                                }

                                return (
                                    <p className="trend-delta" style={{ color: deltaColor(value) }}>
                                        {content}
                                    </p>
                                );
                            };

                            return (
                                <div className="trend-analysis-tile">
                                    <h3>Trend Analysis</h3>
                                    <p className="trend-subtitle">
                                        {hasPreviousPeriodData ? 'Compared to Previous Period' : 'No previous-period data yet'}
                                    </p>
                                    <div className="trend-stat-row">
                                        <p className="stat-title">Unit Volume</p>
                                        {renderDelta(`${deltaSign(volDelta)}${volDelta}`, volDelta)}
                                    </div>
                                    <div className="trend-stat-row">
                                        <p className="stat-title">Sell-Through Rate</p>
                                        {renderDelta(`${deltaSign(strDelta)}${Number(strDelta.toFixed(1))}%`, strDelta)}
                                    </div>
                                    <div className="trend-stat-row">
                                        <p className="stat-title">Average Price</p>
                                        {renderDelta(`${avgDelta < 0 ? '-' : avgDelta > 0 ? '+' : ''}${formatPrice(Math.abs(avgDelta))}`, avgDelta)}
                                    </div>
                                    <div className="trend-stat-row">
                                        <p className="stat-title">Low</p>
                                        {renderDelta(`${lowDelta < 0 ? '-' : lowDelta > 0 ? '+' : ''}${formatPrice(Math.abs(lowDelta))}`, lowDelta)}
                                    </div>
                                    <div className="trend-stat-row">
                                        <p className="stat-title">High</p>
                                        {renderDelta(`${highDelta < 0 ? '-' : highDelta > 0 ? '+' : ''}${formatPrice(Math.abs(highDelta))}`, highDelta)}
                                    </div>
                                </div>
                            );
                        })()}
                    </section>

                    {/* Right Section */}
                    <section className="right-section">
                        <div className="placeholder-chart-container">
                            {loading ? (
                                <p>Loading auction data...</p>
                            ) : (
                                <>
                                    <div className="chart-legend">
                                        <div className="legend-item">
                                            <div className="legend-dot sold"></div>
                                            <span>Sold</span>
                                        </div>
                                        <div className="legend-item">
                                            <div className="legend-dot not-sold"></div>
                                            <span>Not Sold</span>
                                        </div>
                                    </div>
                                    <AuctionChart
                                        auctionData={filteredData}
                                        timeframe={timeframe}
                                        allData={completedAuctions}
                                        onDataPointHover={setHoveredAuction}
                                        sortBy={sortBy}
                                        hoveredFilter={hoveredFilter}
                                        showConditionBands={showConditionBands && modelData?.estimator === 'condition'}
                                        conditionBands={conditionBands}
                                        showMileageBands={showMileageBands && modelData?.estimator === 'mileage'}
                                        mileageRegression={mileageRegression}
                                    />
                                    <div className="stats-row">
                                        <div className="stat-item">
                                            <p className="stat-title">Unit Volume</p>
                                            <p className="stat-value">{filteredData.length}</p>
                                        </div>
                                        <div className="stat-item">
                                            <p className="stat-title">Sell-Through Rate</p>
                                            <p className="stat-value">{`${safeStats.sellThroughRate}%`}</p>
                                        </div>
                                        <div className="stat-item">
                                            <p className="stat-title">Average Price</p>
                                            <p className="stat-value">{formatPrice(safeStats.averagePrice)}</p>
                                        </div>
                                        <div className="stat-item">
                                            <p className="stat-title">Low</p>
                                            <p className="stat-value">{formatPrice(safeStats.lowestPrice)}</p>
                                        </div>
                                        <div className="stat-item">
                                            <p className="stat-title">High</p>
                                            <p className="stat-value">{formatPrice(safeStats.highestPrice)}</p>
                                        </div>
                                    </div>
                                    <hr className="filter-separator" />
                                    <div className="filter-section">
                                        {Object.entries(uniqueValues).map(([category, values]) => {
                                            // Get available values for this category in current timeframe
                                            const availableValues = values.filter(value => {
                                                return getTimeframeValues[category]?.has(value);
                                            });

                                            return availableValues.length > 0 ? (
                                                <div key={category} className="filter-category">
                                                    <h3>{categoryDisplayNames[category] || category.replace(/([A-Z])/g, ' $1').trim()}</h3>
                                                    {availableValues.map(value => (
                                                        <div key={`${category}-${value}`} className="filter-item">
                                                            <input
                                                                type="checkbox"
                                                                checked={filters[category].has(value)}
                                                                onChange={(e) => handleFilterChange(category, value, e.target.checked)}
                                                                className={isHighlighted(category, value) ? 'highlighted' : ''}
                                                                onMouseEnter={() => setHoveredFilter({ category, value })}
                                                                onMouseLeave={() => setHoveredFilter({ category: null, value: null })}
                                                            />
                                                            <label
                                                                className={isHighlighted(category, value) ? 'highlighted' : ''}
                                                                onMouseEnter={() => setHoveredFilter({ category, value })}
                                                                onMouseLeave={() => setHoveredFilter({ category: null, value: null })}
                                                            >
                                                                {getDisplayValue(category, value)}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : null;
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </main>
            </div>
        </section>
    );
};

export default PcaScreenshots;
